import { imageIcon, templateIcon } from '../../../../common/Icons';
import { AntTooltip } from '../../../../common/AntTooltip';
import { useState } from 'react';
import CreateTemplate from './CreateTemplate';
import { Popover } from 'antd';
import UseTemplate from './UseTemplate';
import { CareCanvasTemplate } from '../../../../../store/types/CareCanvas';
import { track } from '../../../../../tracking';

const CareCanvasBottomActionStrip: React.FC<{
    onUpload: () => void;
    onClear: () => void;
    onPrint: () => void;
    hideStrip?: boolean;
    isSaveDisabled?: boolean;
    uploadedImage?: string;
    finalImage?: string;
    markerAreaState: any;
    docid: string;
    isPrintingCanvas?: boolean;
    isSavingCanvas?: boolean;
    onContinue: () => void;
    applyTemplate: (template: CareCanvasTemplate) => void;
    setPreviewTemplate: (template?: CareCanvasTemplate) => void;
    useTemplateVisible?: boolean;
    setUseTemplateVisible: (visible: boolean) => void;
}> = ({
    onUpload,
    onClear,
    onPrint,
    hideStrip,
    isSaveDisabled,
    uploadedImage,
    finalImage,
    markerAreaState,
    docid,
    isPrintingCanvas,
    isSavingCanvas,
    onContinue,
    applyTemplate,
    setPreviewTemplate,
    useTemplateVisible,
    setUseTemplateVisible,
}) => {
    const [createTemplateVisible, setCreateTemplateVisible] = useState(false);

    const onCreateTemplateVisibleChange = (visible: boolean): void => {
        setCreateTemplateVisible(visible);
        track('ON_CREATE_TEMPLATE_VISIBLE_CHANGE', { visible: visible ? 'false' : 'true' });
    };

    const onUseTemplateVisibleChange = (visible: boolean): void => {
        setUseTemplateVisible(visible);
        track('ON_USE_TEMPLATE_VISIBLE_CHANGE', { visible: visible ? 'false' : 'true' });
    };

    return (
        <div
            className={`relative flex items-center justify-between rounded-8 bg-actionPrimary px-16 py-8 cta-row ${
                hideStrip ? 'invisible' : 'visible'
            }`}
        >
            <button className="text-white font-700" onClick={onClear}>
                Clear
            </button>
            <div className="absolute flex items-center space-x-4 left-1/2 transform -translate-x-1/2">
                <AntTooltip content="Upload Image">
                    <button className="p-12" onClick={onUpload}>
                        {imageIcon('w-14 h-13 fill-white transform scale-125')}
                    </button>
                </AntTooltip>
                <Popover
                    content={
                        <UseTemplate
                            onCancel={() => setUseTemplateVisible(false)}
                            applyTemplate={applyTemplate}
                            docid={docid}
                            setPreviewTemplate={setPreviewTemplate}
                        />
                    }
                    trigger={'click'}
                    overlayClassName="popover-p-0 patient-health-summary-popover apply-template-popover"
                    placement="top"
                    destroyTooltipOnHide
                    visible={useTemplateVisible}
                    onVisibleChange={onUseTemplateVisibleChange}
                >
                    <AntTooltip content="Apply Template">
                        <button className="p-12" onClick={() => setUseTemplateVisible(true)}>
                            {templateIcon('w-16 h-16 fill-white')}
                        </button>
                    </AntTooltip>
                </Popover>
            </div>
            <div className="flex items-center space-x-16">
                <Popover
                    content={
                        <CreateTemplate
                            uploadedImage={uploadedImage}
                            finalImage={finalImage}
                            markerAreaState={markerAreaState}
                            onCancel={() => setCreateTemplateVisible(false)}
                            docid={docid}
                        />
                    }
                    trigger={'click'}
                    overlayClassName="popover-p-0 patient-health-summary-popover"
                    placement="top"
                    destroyTooltipOnHide
                    visible={createTemplateVisible}
                    onVisibleChange={onCreateTemplateVisibleChange}
                >
                    <AntTooltip content="Save Template">
                        <div>
                            <button
                                className="px-12 py-9 border border-white rounded-8 disabled:opacity-70 disabled:cursor-not-allowed"
                                disabled={isSaveDisabled}
                                onClick={() => setCreateTemplateVisible(true)}
                            >
                                <svg
                                    className="w-16 h-16 fill-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 448 512"
                                >
                                    <path d="M224 256c-35.2 0-64 28.8-64 64c0 35.2 28.8 64 64 64c35.2 0 64-28.8 64-64C288 284.8 259.2 256 224 256zM433.1 129.1l-83.9-83.9C341.1 37.06 328.8 32 316.1 32H64C28.65 32 0 60.65 0 96v320c0 35.35 28.65 64 64 64h320c35.35 0 64-28.65 64-64V163.9C448 151.2 442.9 138.9 433.1 129.1zM128 80h144V160H128V80zM400 416c0 8.836-7.164 16-16 16H64c-8.836 0-16-7.164-16-16V96c0-8.838 7.164-16 16-16h16v104c0 13.25 10.75 24 24 24h192C309.3 208 320 197.3 320 184V83.88l78.25 78.25C399.4 163.2 400 164.8 400 166.3V416z"></path>
                                </svg>
                            </button>
                        </div>
                    </AntTooltip>
                </Popover>
                <AntTooltip content="Print">
                    <div>
                        <button
                            onClick={onPrint}
                            className="px-12 py-9 border border-white rounded-8 disabled:opacity-70 disabled:cursor-not-allowed"
                            disabled={isSaveDisabled || isPrintingCanvas}
                        >
                            {isPrintingCanvas ? (
                                <svg
                                    className="w-16 h-16 fill-white animate-spin"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                >
                                    <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                </svg>
                            ) : (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-16 h-16 fill-white"
                                    viewBox="0 0 512 512"
                                >
                                    <path d="M128 0C92.7 0 64 28.7 64 64v96h64V64H354.7L384 93.3V160h64V93.3c0-17-6.7-33.3-18.7-45.3L400 18.7C388 6.7 371.7 0 354.7 0H128zM384 352v32 64H128V384 368 352H384zm64 32h32c17.7 0 32-14.3 32-32V256c0-35.3-28.7-64-64-64H64c-35.3 0-64 28.7-64 64v96c0 17.7 14.3 32 32 32H64v64c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V384zm-16-88c-13.3 0-24-10.7-24-24s10.7-24 24-24s24 10.7 24 24s-10.7 24-24 24z" />
                                </svg>
                            )}
                        </button>
                    </div>
                </AntTooltip>
                <button
                    onClick={onContinue}
                    className="px-16 py-8 btn--small primary-btn-v2 disabled:opacity-70 disabled:cursor-not-allowed"
                    disabled={isSavingCanvas}
                >
                    <div className="flex items-center justify-center w-16 h-16">
                        {isSavingCanvas ? (
                            <svg
                                className="w-16 h-16 fill-actionPrimary animate-spin"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                            >
                                <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                            </svg>
                        ) : (
                            <svg
                                className="w-16 h-16 fill-actionPrimary"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 448 512"
                                style={{ rotate: '180deg' }}
                            >
                                <path d="M264.6 70.63l176 168c4.75 4.531 7.438 10.81 7.438 17.38s-2.688 12.84-7.438 17.38l-176 168c-9.594 9.125-24.78 8.781-33.94-.8125c-9.156-9.5-8.812-24.75 .8125-33.94l132.7-126.6H24.01c-13.25 0-24.01-10.76-24.01-24.01s10.76-23.99 24.01-23.99h340.1l-132.7-126.6C221.8 96.23 221.5 80.98 230.6 71.45C239.8 61.85 254.1 61.51 264.6 70.63z"></path>
                            </svg>
                        )}
                    </div>
                    <p>Continue To Prescription</p>
                </button>
            </div>
        </div>
    );
};

export default CareCanvasBottomActionStrip;
